import React, { useEffect, useState } from 'react';
import { Alert, Button, Center, Divider, Group, Loader, Paper, PasswordInput, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconCloudDownload, IconFile, IconLock, IconShieldCheck } from '@tabler/icons-react';
import { useParams } from 'react-router-dom';
import { fileShareHttp } from '../../components/FileShare/service/fileshareHttp';
import { formatBytes, formatDate, totalSize } from '../../components/FileShare/service/fileShareApi';

export default function PublicDownloadPage() {
  const { token } = useParams();
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setShare(null);
    setError('');
    setMessage('');
    fileShareHttp.publicInfo(token)
      .then((data) => { if (alive) setShare(data); })
      .catch((e) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const unlock = async (event) => {
    event.preventDefault();
    setVerifying(true);
    setError('');
    try {
      const data = await fileShareHttp.verify(token, password);
      setShare(data);
      setPassword('');
    } catch (e) {
      setError(e.message);
    } finally {
      setVerifying(false);
    }
  };

  const download = (file = null) => {
    if (!share || share.status !== 'active' || !Array.isArray(share.files)) return;
    // Sunucunun FileResponse yanıtı dosyayı aktarır. JS belleğinde büyük blob biriktirmeyiz.
    // Gizli iframe sayesinde indirme sırasında SPA'den ayrılmayız.
    const url = file
      ? fileShareHttp.downloadUrl(token, file.id)
      : fileShareHttp.zipUrl(token);
    const frame = document.createElement('iframe');
    frame.style.display = 'none';
    frame.title = 'Dosya indirme';
    frame.src = url;
    document.body.appendChild(frame);
    window.setTimeout(() => frame.remove(), 3_600_000);
    setMessage('İndirme isteği gönderildi. Başlamazsa bağlantı süresini, indirme limitini ve tarayıcı izinlerini kontrol edin.');
  };

  const status = share?.status;
  const locked = Boolean(share?.passwordRequired && !Array.isArray(share?.files));

  return <div className="fs-public-page">
    <header className="fs-public-header">
      <div className="fs-brand"><div className="fs-brand-logo"><IconCloudDownload size={24}/></div><div><div className="fs-brand-name">SMS <strong>FileShare</strong></div><div className="fs-brand-subtitle">GÜVENLİ DOSYA PAYLAŞIMI</div></div></div>
      <div className="fs-public-header-right"><IconShieldCheck size={17}/> Süreli erişim</div>
    </header>
    <main className="fs-public-main"><Paper className="fs-public-card" p={{ base: 'lg', sm: 'xl' }}>
      {loading ? <Center py="xl"><Loader/></Center>
        : (!share || status !== 'active') ? <Stack align="center" ta="center" gap="md" py="xl">
          <div className="fs-public-issue"><IconAlertTriangle size={35}/></div>
          <Title order={2}>{status === 'expired' ? 'Bağlantının süresi doldu' : status === 'revoked' ? 'Bağlantı iptal edildi' : status === 'limited' ? 'İndirme limiti doldu' : 'Bağlantı bulunamadı'}</Title>
          <Text c="dimmed" size="sm">Bu bağlantı üzerinden dosyalara erişilemiyor.</Text>
          {error && <Alert color="red">{error}</Alert>}
        </Stack>
        : locked ? <Stack gap="lg">
          <Center><div className="fs-public-lock"><IconLock size={36}/></div></Center>
          <Title order={2} ta="center">Parola korumalı paylaşım</Title>
          <Text size="sm" c="dimmed" ta="center">Dosyaları görüntülemek için göndericinin belirlediği parolayı girin.</Text>
          <form onSubmit={unlock}>
            <PasswordInput label="Paylaşım parolası" placeholder="Parolanız" value={password} onChange={(e) => setPassword(e.currentTarget.value)} autoFocus required/>
            <Button type="submit" fullWidth mt="md" loading={verifying}>Dosyaları görüntüle</Button>
          </form>
          {error && <Alert color="red">{error}</Alert>}
          <Text size="xs" c="dimmed" ta="center">Son erişim: {formatDate(share.expiresAt)}</Text>
        </Stack>
        : <Stack gap="lg">
          <Center><div className="fs-public-ready"><IconCheck size={29}/></div></Center>
          <div><Text size="xs" c="indigo" fw={800}>SMS Grup İnşaat A.Ş.</Text><Title order={2} mt="xs">{share.title}</Title><Text c="dimmed" size="sm">{share.note || 'Sizinle dosya paylaşıldı.'}</Text></div>
          <div className="fs-public-meta">
            <div><Text size="xs" c="dimmed">DOSYA</Text><Text fw={800}>{share.files.length}</Text></div>
            <div><Text size="xs" c="dimmed">TOPLAM BOYUT</Text><Text fw={800}>{formatBytes(totalSize(share.files))}</Text></div>
            <div><Text size="xs" c="dimmed">SON ERİŞİM</Text><Text fw={800}>{formatDate(share.expiresAt)}</Text></div>
          </div>
          <Divider/><Text fw={800} size="sm">Paylaşılan Dosyalar</Text>
          {share.files.map((file) => <Group key={file.id} justify="space-between" wrap="nowrap" className="fs-public-file">
            <div className="fs-file-icon"><IconFile size={19}/></div>
            <div style={{ flex: 1, minWidth: 0 }}><Text size="sm" fw={700} truncate>{file.name}</Text><Text size="xs" c="dimmed">{formatBytes(file.size)}</Text></div>
            <Button size="xs" variant="light" onClick={() => download(file)} leftSection={<IconCloudDownload size={16}/>}>İndir</Button>
          </Group>)}
          <Button onClick={() => download(share.files.length === 1 ? share.files[0] : null)} leftSection={<IconCloudDownload size={18}/>} fullWidth>{share.files.length > 1 ? 'Tümünü ZIP Olarak İndir' : 'Dosyayı İndir'}</Button>
          {message && <Alert color="blue">{message}</Alert>}
          <Text ta="center" size="xs" c="dimmed">Bağlantı süresi ve parola sunucu tarafından doğrulanır.</Text>
        </Stack>}
    </Paper></main>
  </div>;
}
